import type { AdminSettings, Airstrip, AirstripDistance } from '../types';

const DEFAULT_SPEED_KTS = 120;
const DEFAULT_DISTANCE_FACTOR = 1.15;
const KTS_TO_MPH = 1.15078;

const toCoordinateMinutes = (value: number) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  const sign = numeric < 0 ? -1 : 1;
  const absolute = Math.abs(numeric);
  const degrees = Math.floor(absolute);
  const minutes = (absolute - degrees) * 60;
  return sign * ((degrees * 60) + minutes);
};

export const computeRouteMatrixDistanceMiles = (
  from: Airstrip,
  to: Airstrip,
  routeDefaults?: AdminSettings['routeDefaults'],
) => {
  const latFromMinutes = toCoordinateMinutes(from.latitude);
  const latToMinutes = toCoordinateMinutes(to.latitude);
  const lonFromMinutes = toCoordinateMinutes(from.longitude);
  const lonToMinutes = toCoordinateMinutes(to.longitude);
  const baseDistance = Math.sqrt(
    ((latFromMinutes - latToMinutes) ** 2) +
    ((lonFromMinutes - lonToMinutes) ** 2)
  );
  const factor = DEFAULT_DISTANCE_FACTOR;
  const baseMiles = Math.max(0, Number(routeDefaults?.distanceBaseMiles || 0));
  const distance = Math.ceil(baseDistance * factor) + baseMiles;
  const normalized = Math.ceil(distance);
  return Number.isFinite(normalized) ? normalized : 0;
};

export const computeRouteMatrixBlockMinutes = (
  distanceMiles: number,
  routeDefaults?: AdminSettings['routeDefaults'],
  overrideSpeedKts?: number,
) => {
  const speedKts = Number(overrideSpeedKts || 0) > 0
    ? Number(overrideSpeedKts || 0)
    : Math.max(0, Number(routeDefaults?.cruiseSpeedKts || DEFAULT_SPEED_KTS));
  const speedMph = speedKts * KTS_TO_MPH;
  if (speedMph <= 0) return 0;
  const rawMinutes = Math.round((distanceMiles / speedMph) * 60);
  return Math.max(1, rawMinutes);
};

export const resolveRouteMatrixMetrics = ({
  from,
  to,
  routeDefaults,
  override,
}: {
  from: Airstrip;
  to: Airstrip;
  routeDefaults?: AdminSettings['routeDefaults'];
  override?: AirstripDistance | null;
}) => {
  const distanceMiles = Number(override?.distanceMiles || 0) > 0
    ? Number(override?.distanceMiles || 0)
    : computeRouteMatrixDistanceMiles(from, to, routeDefaults);
  const computedBlockMinutes = computeRouteMatrixBlockMinutes(
    distanceMiles,
    routeDefaults,
    Number(override?.overrideSpeedKts || 0),
  );
  const overrideBlockMinutes = Number(override?.overrideBlockMinutes || 0);
  const blockMinutes = overrideBlockMinutes > 0 ? Math.round(overrideBlockMinutes) : computedBlockMinutes;
  return {
    distanceMiles,
    blockMinutes,
  };
};

export const routeMatrixKtsToMph = KTS_TO_MPH;
